/*
 * Kitchen — Mətbəx ekranı
 *
 * Mutfak personeli için aktif sipariş ticket'larını yatay scroll grid'inde
 * gösterir. `kitchen/KitchenPage.tsx` eski örneğinden esinlenildi ama
 * performans için yeniden yazıldı:
 *
 *   • motion `layout` prop'u kaldırıldı (FLIP recomputation aşırı pahalı)
 *   • whileHover/whileTap kaldırıldı, yerine `pos-press` CSS sınıfı
 *   • Ticket kartı React.memo ile sarmalandı
 *   • Polling sırasında veri değişmediyse setTickets çağrılmıyor
 *     (avantaj: gereksiz render zinciri yok)
 *   • Spinner sadece "ilk yükleme"de görünür; sonraki polling'lerde
 *     görünmez (Wolt/macOS gibi sessiz refetch)
 *   • Background blur orb'lar tek pass paint için pointer-events-none
 *   • Inline style ile var(--bg-body) → dark mode'da tek renk
 *
 * API:
 *   GET   /api/kitchens/{kitchenId}/display
 *   PATCH /api/kitchens/{kitchenId}/orders/{orderId}/status
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "motion/react";
import { notify, type NotifyKind } from '../components/ui/notify';
import {
  ChefHat,
  CheckCircle2,
  AlertTriangle,
  Flame,
  Package,
  Store,
  Truck,
  X,
  Settings,
  RefreshCw,
  Volume2,
  VolumeX,
  Lock,
} from "lucide-react";
import { useWaiterCallsRealtime } from "../hook/useWaiterCallsRealtime";
import { usePosOrderRealtime } from "../hook/usePosOrderRealtime";
import { useKitchenAlert } from "../hook/useKitchenAlert";
import {
  getKitchenDisplay,
  getKitchenArchive,
  getKitchens,
  updateKitchenOrderStatus,
  shortRef,
  type KitchenTicket,
  type KitchenOrderStatus,
  type OrderRef,
} from "../api/kitchen";
import { PosLoader } from "../components/pos/PosLoader";
import { useI18n } from "../i18n/I18nContext";
import { usePermissions } from "../context/PermissionsContext";

type TFn = (key: string, vars?: Record<string, string | number>) => string;

// ---------------------------------------------------------------------------
// Sabitler
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 8000;
const URGENCY_WARNING_MIN = 10;
const URGENCY_CRITICAL_MIN = 15;
const KITCHEN_ID_STORAGE_KEY = "kitchen_id";

/**
 * Kitchen ID dinamik kaynağı. Kaç senaryo:
 *   1. URL `?kitchenId=N` query param  — manuel override / test için
 *   2. localStorage `kitchen_id`        — kullanıcı daha önce ayarlamışsa
 *   3. localStorage `restaurant_info.id` — 1 restoran : 1 mutfak senaryosu
 *      (kullanıcının bahsettiği: QR `/order-details/[id]` üzerindeki id
 *       restorana ait — aynı id mutfak için de kullanılıyor)
 *   4. Fallback: 1
 */
const resolveKitchenId = (): number => {
  try {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("kitchenId");
      if (fromUrl && Number.isFinite(Number(fromUrl))) return Number(fromUrl);
    }
  } catch {
    /* ignore */
  }
  try {
    const stored = localStorage.getItem(KITCHEN_ID_STORAGE_KEY);
    if (stored && Number.isFinite(Number(stored))) return Number(stored);
  } catch {
    /* ignore */
  }
  try {
    const raw = localStorage.getItem("restaurant_info");
    if (raw) {
      const parsed = JSON.parse(raw);
      const id = Number(parsed?.id);
      if (Number.isFinite(id) && id > 0) return id;
    }
  } catch {
    /* ignore */
  }
  return 1;
};

/** Lövhə sütunları — mətbəxin iş axını soldan sağa. */
const BOARD_COLUMNS = [
  { status: "pending" as const, labelKey: "kitchen.filter.pending", accent: "bg-gray-400" },
  { status: "preparing" as const, labelKey: "kitchen.filter.preparing", accent: "bg-blue-500" },
  { status: "ready" as const, labelKey: "kitchen.filter.ready", accent: "bg-emerald-500" },
];

/** Arxiv üçün hazır tarix aralıqları. */
const ARCHIVE_RANGES = [
  { key: "today" as const, labelKey: "kitchen.range.today", days: 0 },
  { key: "yesterday" as const, labelKey: "kitchen.range.yesterday", days: 1 },
  { key: "week" as const, labelKey: "kitchen.range.week", days: 7 },
  { key: "month" as const, labelKey: "kitchen.range.month", days: 30 },
];
type ArchiveRangeKey = (typeof ARCHIVE_RANGES)[number]["key"];

/** Seçilmiş aralığı [from, to] ms-ə çevir. */
const rangeToMs = (key: ArchiveRangeKey): { from: number; to: number } => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const DAY = 24 * 60 * 60 * 1000;
  if (key === "today") return { from: startOfToday, to: Date.now() };
  if (key === "yesterday") return { from: startOfToday - DAY, to: startOfToday - 1 };
  const days = key === "week" ? 7 : 30;
  return { from: startOfToday - (days - 1) * DAY, to: Date.now() };
};

// ---------------------------------------------------------------------------
// Yardımcılar
// ---------------------------------------------------------------------------

/** İki ticket dizisinin "anlamlı" eşitliği — id, status ve item count'a bakar.
 *  Backend her poll'de yeni nesne döndürüyor; bu yüzden referans karşılaştırması
 *  yetmez. Bu helper sayesinde "değişmemiş" datada setState çağrısı atılmıyor. */
const ticketsEqual = (a: KitchenTicket[], b: KitchenTicket[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.overall_status !== y.overall_status ||
      x.items.length !== y.items.length || x.items.some((it, i) => it.status !== y.items[i]?.status) ||
      x.order_number !== y.order_number
    ) {
      return false;
    }
  }
  return true;
};

const elapsedMinutes = (iso: string): number => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
};

const channelMeta = (type: KitchenTicket["type"], t: TFn, table?: string | null) => {
  switch (type) {
    case "takeaway":
      return { icon: <Package className="w-4 h-4" />, label: t("kitchen.channel.takeaway") };
    case "delivery":
      return { icon: <Truck className="w-4 h-4" />, label: t("kitchen.channel.delivery") };
    default:
      return { icon: <Store className="w-4 h-4" />, label: table || t("kitchen.channel.hall") };
  }
};

const statusMeta = (status: KitchenOrderStatus, t: TFn) => {
  switch (status) {
    case "preparing":
      return {
        label: t("kitchen.status.preparing"),
        dotClass: "bg-blue-500 animate-pulse",
        chipClass:
          "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
      };
    case "ready":
      return {
        label: t("kitchen.status.ready"),
        dotClass: "bg-emerald-500",
        chipClass:
          "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
      };
    case "cancelled":
      return {
        label: t("kitchen.status.cancelled"),
        dotClass: "bg-red-500",
        chipClass:
          "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
      };
    default:
      return {
        label: t("kitchen.status.pending"),
        dotClass: "bg-gray-400",
        chipClass:
          "bg-gray-50 dark:bg-[#1a1b1e]0/10 text-gray-700 dark:text-gray-300 border-gray-500/20",
      };
  }
};

// ---------------------------------------------------------------------------
// Ticket kartı — saf component, React.memo ile sarmalandı
// ---------------------------------------------------------------------------
interface TicketCardProps {
  ticket: KitchenTicket;
  onStatusChange: (
    orderId: OrderRef,
    next: KitchenOrderStatus,
  ) => Promise<void> | void;
  /**
   * `kitchen.update-status` icazəsi. Yalnız "Mətbəx ekranına bax" verilmiş
   * işçi lövhəni oxuyur, amma statusu dəyişə bilmir.
   */
  canUpdateStatus: boolean;
}

const TicketCardImpl: React.FC<TicketCardProps> = ({
  ticket,
  onStatusChange,
  canUpdateStatus,
}) => {
  const { t } = useI18n();
  // 60 sn'de bir minute counter güncellenir.
  const [elapsed, setElapsed] = useState(() =>
    elapsedMinutes(ticket.created_at),
  );
  const [busy, setBusy] = useState<KitchenOrderStatus | null>(null);

  useEffect(() => {
    setElapsed(elapsedMinutes(ticket.created_at));
    const id = setInterval(
      () => setElapsed(elapsedMinutes(ticket.created_at)),
      60000,
    );
    return () => clearInterval(id);
  }, [ticket.created_at]);

  const urgency: "normal" | "warning" | "critical" =
    elapsed >= URGENCY_CRITICAL_MIN
      ? "critical"
      : elapsed >= URGENCY_WARNING_MIN
        ? "warning"
        : "normal";

  const channel = channelMeta(ticket.type, t, ticket.table);
  const status = statusMeta(ticket.overall_status, t);

  const handle = useCallback(
    async (next: KitchenOrderStatus) => {
      setBusy(next);
      try {
        await onStatusChange(ticket.order_id, next);
      } finally {
        setBusy(null);
      }
    },
    [onStatusChange, ticket.order_id],
  );

  // Vaxt rəngi: normal → sakit, gecikmə → narıncı, kritik → qırmızı.
  const urgencyText =
    urgency === 'critical'
      ? 'text-red-600 dark:text-red-400'
      : urgency === 'warning'
        ? 'text-orange-600 dark:text-orange-400'
        : 'text-gray-400 dark:text-gray-500';

  // İş axını: gözləyən → hazırlanır → servisə hazır → TƏHVİL ALINDI.
  // Sonuncu addım ticket-i lövhədən çıxarıb arxivə göndərir.
  //
  // İCAZƏ YOXDURSA DÜYMƏ YOXDUR. `canUpdateStatus` false olanda `primary`
  // null qalır və aşağıdakı budaq oxu-rejimi göstərir — düyməni "disabled"
  // etmək kifayət deyildi, çünki ekranda hələ də edilə bilən bir iş kimi
  // görünürdü.
  const primary =
    !canUpdateStatus
    ? null
    : ticket.overall_status === 'pending'
      ? { label: t('kitchen.acceptStart'), next: 'preparing' as const, cls: 'bg-blue-600 hover:bg-blue-700' }
      : ticket.overall_status === 'preparing'
        ? { label: t('kitchen.sendToService'), next: 'ready' as const, cls: 'bg-emerald-600 hover:bg-emerald-700' }
        : ticket.overall_status === 'ready'
          ? { label: t('kitchen.handedOver'), next: 'served' as const, cls: 'bg-gray-800 hover:bg-gray-900 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-white' }
          : null;

  return (
    <article
      className="flex flex-col rounded-2xl border border-black/[0.07] dark:border-white/10 bg-white dark:bg-[#1a1b1e] overflow-hidden"
      style={{ backgroundColor: 'var(--bg-card, #ffffff)' }}
    >
      {/* Status = nazik üst zolaq. Ayrıca status sətri artıq yoxdur. */}
      <div className={`h-1 ${status.dotClass.split(' ')[0]}`} />

      {/* Başlıq: MASA adı əsasdır — aşpaz sifariş id-sinə deyil, masaya baxır. */}
      <header className="px-4 pt-3.5 pb-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-bold text-[15px] text-gray-900 dark:text-white truncate">
            {channel.label}
          </h3>
          <span className={`text-xs font-semibold shrink-0 pos-tabular ${urgencyText}`}>
            {t('kitchen.min', { n: elapsed })}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400 dark:text-gray-500">
          <span className="pos-tabular">#{shortRef(ticket.order_id)}</span>
          <span aria-hidden>·</span>
          <span className="truncate">{status.label}</span>
        </div>
      </header>

      {/* Sətirlər — sadə siyahı, dekorasiya yox. */}
      <ul className="px-4 pb-3 space-y-1.5">
        {ticket.items.map((item, idx) => (
          <li key={`${item.id}-${idx}`} className="flex gap-2.5">
            <span className="shrink-0 min-w-[1.6rem] h-6 px-1.5 rounded-md bg-black/[0.05] dark:bg-white/10 text-[12px] font-bold text-gray-700 dark:text-gray-200 flex items-center justify-center pos-tabular">
              {item.quantity}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] leading-6 text-gray-900 dark:text-gray-100 break-words">
                {item.name}
              </p>
              {item.notes && (
                <p className="mt-0.5 text-[12px] leading-snug text-orange-600 dark:text-orange-400 break-words">
                  {item.notes}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Əməliyyat — tək əsas düymə; ləğv sakit mətn düyməsidir. */}
      {primary ? (
        <div className="px-4 pb-4 flex items-center gap-2">
          <button
            type="button"
            disabled={!!busy}
            onClick={() => handle(primary.next)}
            className={`pos-press flex-1 h-10 rounded-xl text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors ${primary.cls}`}
          >
            {busy === primary.next ? <PosLoader size="sm" color="#ffffff" /> : primary.label}
          </button>
          <button
            type="button"
            disabled={!!busy}
            onClick={() => handle('cancelled')}
            title={t('common.cancel')}
            className="pos-press w-10 h-10 rounded-xl border border-black/[0.07] dark:border-white/10 text-gray-400 hover:text-red-500 hover:border-red-500/30 flex items-center justify-center disabled:opacity-50 transition-colors"
          >
            {busy === 'cancelled' ? <PosLoader size="sm" color="#ef4444" /> : <X className="w-4 h-4" />}
          </button>
        </div>
      ) : !canUpdateStatus &&
        ticket.overall_status !== 'ready' &&
        ticket.overall_status !== 'cancelled' ? (
        /* Oxu rejimi — icazə yoxdur. Statusu göstəririk, düymə göstərmirik. */
        <div className="px-4 pb-4">
          <div
            className="h-10 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold bg-black/[0.04] dark:bg-white/[0.06] text-gray-500 dark:text-gray-400"
            title={t('kitchen.noStatusPermission')}
          >
            <Lock className="w-3.5 h-3.5" />
            {t('kitchen.readOnly')}
          </div>
        </div>
      ) : (
        <div className="px-4 pb-4">
          <div
            className={`h-10 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold ${
              ticket.overall_status === 'ready'
                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                : 'bg-red-500/10 text-red-600 dark:text-red-400'
            }`}
          >
            {ticket.overall_status === 'ready' ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                {t('kitchen.readyToServe')}
              </>
            ) : (
              <>
                <X className="w-4 h-4" />
                {t('kitchen.cancelled')}
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

const TicketCard = React.memo(TicketCardImpl, (prev, next) => {
  // Yalnızca anlamlı alanlar değiştiyse re-render — aynı ticket id + status +
  // item count + order_number ise polling tetiklediği "yeni nesne"yi atla.
  const a = prev.ticket;
  const b = next.ticket;
  return (
    a.id === b.id &&
    a.overall_status === b.overall_status &&
    a.items.length === b.items.length &&
    a.order_number === b.order_number &&
    a.created_at === b.created_at &&
    prev.onStatusChange === next.onStatusChange &&
    prev.canUpdateStatus === next.canUpdateStatus
  );
});

// ---------------------------------------------------------------------------
// Sayfa
// ---------------------------------------------------------------------------

const Kitchen: React.FC = () => {
  const { t } = useI18n();
  const { hasPermission } = usePermissions();

  // ── "Mətbəx ekranına bax" ilə "Yemək statusunu dəyiş" AYRI icazələrdir ──
  //
  // Əvvəl bu ekranda heç bir icazə yoxlanmırdı: mətbəx səhifəsini aça bilən
  // hər kəs statusu dəyişə bilirdi. Admin paneldə yalnız "bax" verilmiş aşpaz
  // yenə də sifarişi "hazır" edirdi.
  //
  // `kitchen.manage` (mətbəx konfiqurasiyası) və `manage-kitchen` umbrella-sı
  // qəbul edilir, çünki hər ikisi status dəyişməkdən daha geniş səlahiyyətdir.
  // `kitchen.display` QƏSDƏN yoxdur — o, sırf oxu icazəsidir.
  const canUpdateStatus = hasPermission([
    "kitchen.update-status",
    "kitchen.manage",
    "manage-kitchen",
  ]);
  // handleStatusChange identity-si sabit qalmalıdır (TicketCard memo-su ona
  // baxır), ona görə icazə asılılıq siyahısına deyil, ref-ə yazılır.
  const canUpdateStatusRef = useRef(canUpdateStatus);
  canUpdateStatusRef.current = canUpdateStatus;

  const [tickets, setTickets] = useState<KitchenTicket[]>([]);
  const [view, setView] = useState<"board" | "archive">("board");
  const [archiveRange, setArchiveRange] = useState<ArchiveRangeKey>("today");
  const [archiveTickets, setArchiveTickets] = useState<KitchenTicket[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  // POS rejimində pos_local mətbəx id-si numerik gəlir, amma cloud/POS qarışıq
  // ola bildiyi üçün tip genişdir.
  const [kitchenId, setKitchenId] = useState<OrderRef>(() => resolveKitchenId());
  const [kitchenName, setKitchenName] = useState<string>("");
  const [errorState, setErrorState] = useState<
    | null
    | { kind: "not_found"; status?: number }
    | { kind: "network"; message: string }
  >(null);
  const [showSettings, setShowSettings] = useState(false);

  const inFlightRef = useRef(0);
  const prevCountRef = useRef(0);
  // localStorage'da kayıtlı ID yoksa, API'den ilk mutfağı otomatik seçme bayrağı
  const autoResolvedRef = useRef(false);
  // Garson çağrısı — duplicate bildirimleri önlemek için görülen call_id'ler
  const seenWaiterCallsRef = useRef<Set<number>>(new Set());

  const kitchenAlert = useKitchenAlert();
  // `loadTickets` useCallback-inin asılılıq siyahısını dəyişməmək üçün ref —
  // əks halda hər toggle polling effekt-ini yenidən qurardı.
  const alertRef = useRef(kitchenAlert.play);
  alertRef.current = kitchenAlert.play;

  // restaurant_info'dan restaurantId oku (waiter call bildirimler için)
  const restaurantId = useMemo(() => {
    try {
      const raw = localStorage.getItem('restaurant_info');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed?.id === 'number' ? parsed.id : parsed?.id ? Number(parsed.id) : null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(KITCHEN_ID_STORAGE_KEY, String(kitchenId));
    } catch {
      /* ignore */
    }
  }, [kitchenId]);

  // Sayfa açılışında localStorage'da kitchen_id yoksa API'den otomatik bul
  useEffect(() => {
    const hasStoredId = !!localStorage.getItem(KITCHEN_ID_STORAGE_KEY);
    if (hasStoredId || autoResolvedRef.current) return;
    autoResolvedRef.current = true;

    (async () => {
      try {
        const kitchens = await getKitchens();
        if (kitchens.length > 0) {
          const first = kitchens[0];
          setKitchenId(first.id);
          setKitchenName(first.name || t("kitchen.name", { id: first.id }));
          try { localStorage.setItem(KITCHEN_ID_STORAGE_KEY, String(first.id)); } catch { /**/ }
        }
      } catch {
        /* API başarısız — resolveKitchenId'nin döndürdüğü default ile devam */
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mətbəx bildirişləri qlobal island-a gedir (src/components/ui/notify.tsx).
  // Əvvəl bu səhifənin öz KitchenIsland-ı var idi və eyni hadisə həm island,
  // həm də sağdan çıxan toast kimi İKİ dəfə görünürdü. İndi tək mənbə var.
  const triggerNotice = useCallback(
    (title: string, message: string, kind: NotifyKind) => {
      notify[kind](message, { title });
    },
    [],
  );

  const loadTickets = useCallback(async () => {
    const ticket = ++inFlightRef.current;
    let switchedKitchen = false;
    try {
      const res = await getKitchenDisplay(kitchenId);
      if (ticket !== inFlightRef.current) return; // başka istek kazanmış
      const next = res.tickets;

      // Aşpaz HANSI mətbəxə baxdığını bilməlidir — başlıqda ad göstərilir.
      if (res.kitchen?.name) setKitchenName(res.kitchen.name);

      setErrorState(null);
      setTickets((cur) => {
        if (ticketsEqual(cur, next)) return cur;
        return next;
      });

      // Yeni sipariş bildirimi — sadece artış olduğunda.
      if (prevCountRef.current > 0 && next.length > prevCountRef.current) {
        const diff = next.length - prevCountRef.current;
        triggerNotice(t("kitchen.notice.newOrderTitle"), t("kitchen.notice.newOrderMsg", { n: diff }), "alert");
        // Aşpaz ekrana baxmır — səs olmadan ticket dəqiqələrlə gözləyə bilər.
        alertRef.current();
      }
      prevCountRef.current = next.length;
    } catch (err: any) {
      const status = err?.response?.status;
      // 404, 403 veya 500: doğru kitchen ID'yi API'den bul
      if (status === 404 || status === 403 || status === 500) {
        try {
          const kitchens = await getKitchens();
          const first = kitchens[0];
          if (first) {
            if (first.id !== kitchenId) {
              switchedKitchen = true;
              prevCountRef.current = 0;
              setTickets([]);
              setErrorState(null);
              setInitialLoading(true);
              setKitchenId(first.id);
              setKitchenName(first.name || t("kitchen.name", { id: first.id }));
              triggerNotice(
                t("kitchen.notice.foundTitle"),
                t("kitchen.notice.foundMsg", { name: first.name || `#${first.id}` }),
                "success",
              );
              return;
            }
          }
        } catch {
          // getKitchens de başarısızsa not_found göster
        }
        setErrorState({ kind: "not_found", status });
        setTickets([]);
      } else if (status !== 401) {
        // 401 sessiz — login akışı handle eder.
        setErrorState({
          kind: "network",
          message: err?.message || t("kitchen.connectFailed"),
        });
        console.warn("[Kitchen] yüklənmədi", err?.message || err);
      }
    } finally {
      if (ticket === inFlightRef.current && !switchedKitchen) {
        setInitialLoading(false);
      }
    }
  }, [kitchenId, triggerNotice, t]);

  // REALTIME: pos_local WS-dən `orders.*` event-i gələn kimi refetch.
  // Ofisant "mətbəxə göndər" edəndə sifariş 8 saniyə gözləmədən DƏRHAL görünür —
  // mətbəxdə bu fərq real əhəmiyyət daşıyır.
  // Yalnız lövhə görünüşündə: arxiv tarixi keçmişdir, canlı yenilənməyə ehtiyacı yoxdur.
  usePosOrderRealtime(loadTickets, view === "board");

  // Polling REALTIME-ın EHTİYAT NÜSXƏSİDİR, əvəzi deyil: WS qopsa yaxud cloud
  // rejimində işləsək ekran yenə də yenilənməlidir.
  useEffect(() => {
    loadTickets();
    const id = window.setInterval(() => {
      // Sekme arka plandayken polling'i dondur.
      if (typeof document !== "undefined" && document.hidden) return;
      loadTickets();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [loadTickets]);

  // Sekme tekrar görünür olduğunda anında refetch.
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) loadTickets();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [loadTickets]);

  // ---------------------------------------------------------------------------
  // Garson çağrısı bildirimleri — mutfak ekranında da göster
  // ---------------------------------------------------------------------------
  useWaiterCallsRealtime({
    restaurantId: restaurantId ?? null,
    onCalled: (ev) => {
      if (!seenWaiterCallsRef.current.has(ev.call_id)) {
        seenWaiterCallsRef.current.add(ev.call_id);
        // Ofisant çağırışı adi bildirişdən uzun qalmalıdır: aşpaz onu
        // görüb ofisanta xəbər verməlidir. toastId dublikatın qarşısını alır.
        notify.alert(`${ev.table_name}: ${ev.reason_label || t("kitchen.waiterReason")}`, {
          title: t("kitchen.notice.waiterTitle"),
          autoClose: 8000,
          toastId: `wc-${ev.call_id}`,
        });
      }
    },
    onStatusChanged: () => { /* mutfak için durum değişimi önemli değil */ },
    onRefresh: () => { /* mutfak kendi polling'ini zaten yapıyor */ },
    pollIntervalMs: 15000,
    disabled: !restaurantId,
  });

  // Status değiştirme handler — kart child'inde useCallback ile kullanılıyor,
  // bu yüzden burada da identity sabit olmalı (memo).
  const handleStatusChange = useCallback(
    async (orderId: OrderRef, next: KitchenOrderStatus) => {
      // İkinci qat. Düymələr onsuz da gizlidir, amma optimistik UI burada
      // baş verir: icazəsiz çağırış lövhəni yerli olaraq "dəyişdi" göstərib
      // yalnız serverin 403-ündən sonra geri qaytarardı.
      if (!canUpdateStatusRef.current) {
        notify.error(t("kitchen.noStatusPermission"));
        return;
      }
      // Optimistic UI: state'i hemen güncelle, başarısız olursa geri al.
      setTickets((cur) =>
        cur.map((t) =>
          t.order_id === orderId ? { ...t, overall_status: next } : t,
        ),
      );
      try {
        await updateKitchenOrderStatus(kitchenId, orderId, next);
        const messages: Record<KitchenOrderStatus, string> = {
          pending: t("kitchen.msg.pending"),
          preparing: t("kitchen.msg.preparing"),
          ready: t("kitchen.msg.ready"),
          served: t("kitchen.msg.served"),
          cancelled: t("kitchen.msg.cancelled"),
        };
        // "ready" üçün ayrıca başlıqlı island; qalan statuslar sadə bildiriş.
        if (next === "ready") {
          triggerNotice(t("kitchen.notice.readyTitle"), messages.ready, "success");
        } else {
          notify.success(messages[next]);
        }
        // Backend asıl source-of-truth → ardından refetch.
        loadTickets();
      } catch (err: any) {
        notify.error(t("kitchen.statusFailed"));
        // Server'dan gerçek state'i geri al.
        loadTickets();
      }
    },
    [kitchenId, loadTickets, triggerNotice, t],
  );

  // Lövhə sütunları — hər status öz sütununda.
  const board = useMemo(() => {
    const byStatus: Record<string, KitchenTicket[]> = {
      pending: [],
      preparing: [],
      ready: [],
    };
    for (const ticket of tickets) {
      (byStatus[ticket.overall_status] ?? byStatus.pending).push(ticket);
    }
    return byStatus;
  }, [tickets]);

  // Arxiv — yalnız "Arxiv" görünüşü açıq olanda çəkilir.
  const loadArchive = useCallback(
    async (key: ArchiveRangeKey) => {
      setArchiveLoading(true);
      try {
        const { from, to } = rangeToMs(key);
        const res = await getKitchenArchive(kitchenId, from, to);
        setArchiveTickets(res.tickets);
      } catch {
        setArchiveTickets([]);
      } finally {
        setArchiveLoading(false);
      }
    },
    [kitchenId],
  );

  useEffect(() => {
    if (view === "archive") void loadArchive(archiveRange);
  }, [view, archiveRange, loadArchive]);

  const counts = useMemo(() => {
    let pending = 0;
    let preparing = 0;
    let ready = 0;
    let critical = 0;
    const now = Date.now();
    for (const t of tickets) {
      if (t.overall_status === "pending") pending++;
      else if (t.overall_status === "preparing") preparing++;
      else if (t.overall_status === "ready") ready++;
      const elapsed = Math.max(
        0,
        Math.floor((now - new Date(t.created_at).getTime()) / 60000),
      );
      if (elapsed >= URGENCY_CRITICAL_MIN && t.overall_status !== "ready")
        critical++;
    }
    return { pending, preparing, ready, critical };
  }, [tickets]);

  return (
    <>
      <Helmet>
        <title>{t("kitchen.pageTitle")}</title>
        <meta name="description" content={t("kitchen.pageDesc")} />
      </Helmet>

      <div
        className="h-[calc(100vh-5rem)] w-full flex flex-col relative overflow-hidden"
        style={{ backgroundColor: "var(--bg-body)" }}
      >

        {/* Background ambient — pointer-events kapalı, tek paint */}
        <div
          aria-hidden
          className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 dark:bg-orange-500/5 blur-[120px] rounded-full pointer-events-none"
        />
        <div
          aria-hidden
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 blur-[120px] rounded-full pointer-events-none"
        />

        {/* Top bar */}
        <div
          className="relative px-4 sm:px-6 py-3 border-b border-black/5 dark:border-white/10 flex flex-wrap items-center justify-between gap-3 z-30"
          style={{ backgroundColor: "var(--bg-body)" }}
        >
          {/* Sol: branding + sayaçlar */}
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-[var(--primary)] flex items-center justify-center shrink-0">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              {/* Başlıq = MƏTBƏXİN ADI. Bir neçə mətbəx olanda aşpaz hansı
                  ekrana baxdığını görməlidir. */}
              <p className="font-bold text-base text-gray-900 dark:text-white truncate">
                {kitchenName || t("kitchen.name", { id: kitchenId })}
              </p>
            </div>

            {/* Sayaclar yalnız geniş ekranda — filtr tabları onsuz da eyni
                məlumatı verir və dar ekranda yer qalmır. */}
            <div className="hidden lg:flex items-center gap-2">
              <StatPill label={t("kitchen.stat.pending")} value={counts.pending} tone="gray" />
              <StatPill
                label={t("kitchen.stat.preparing")}
                value={counts.preparing}
                tone="blue"
              />
              <StatPill label={t("kitchen.stat.ready")} value={counts.ready} tone="green" />
            </div>
          </div>

          {/* Sağ: filter + alert + settings */}
          <div className="flex items-center gap-2 min-w-0 ml-auto">
            <div
              className="pos-tabstrip flex p-1 rounded-xl border border-black/5 dark:border-white/10 overflow-x-auto pos-scroll"
              style={{ backgroundColor: "var(--bg-elevated)" }}
            >
              {/* Status filtri yerinə GÖRÜNÜŞ keçidi: statuslar onsuz da
                  lövhənin sütunlarıdır, ayrıca filtr artıq lazım deyil. */}
              {(["board", "archive"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`pos-press px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-colors ${view === v
                    ? "bg-[var(--primary)] text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                >
                  {t(v === "board" ? "kitchen.view.board" : "kitchen.view.archive")}
                </button>
              ))}
            </div>

            {counts.critical > 0 && (
              <div
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-red-500/10 shrink-0"
                title={t("kitchen.urgent")}
              >
                <Flame className="w-4 h-4 text-red-500" />
                <span className="font-bold text-sm text-red-600 dark:text-red-400 pos-tabular">
                  {counts.critical}
                </span>
              </div>
            )}

            {/* Səs siqnalı açıq/bağlı — seçim localStorage-də qalır. */}
            <button
              type="button"
              onClick={kitchenAlert.toggle}
              title={t(kitchenAlert.enabled ? "kitchen.sound.on" : "kitchen.sound.off")}
              aria-pressed={kitchenAlert.enabled}
              className={`pos-press shrink-0 w-9 h-9 rounded-xl border flex items-center justify-center transition-colors ${
                kitchenAlert.enabled
                  ? "border-[var(--primary)]/30 bg-[var(--primary)]/10 text-[var(--primary)]"
                  : "border-black/5 dark:border-white/10 text-gray-400 dark:text-gray-500"
              }`}
            >
              {kitchenAlert.enabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </button>

            {/* Manuel kitchen ID seti */}
            <KitchenIdControl
              currentId={kitchenId}
              isOpen={showSettings}
              onToggle={() => setShowSettings((v) => !v)}
              onClose={() => setShowSettings(false)}
              onChange={(next) => {
                setKitchenId(next);
                setShowSettings(false);
                setInitialLoading(true);
                setErrorState(null);
                setTickets([]);
                prevCountRef.current = 0;
              }}
              onRefresh={() => loadTickets()}
            />
          </div>
        </div>

        {/* Tickets — yatay scroll grid */}
        <div className="flex-1 overflow-y-auto pos-scroll relative">
          {initialLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <PosLoader size="lg" label={t("kitchen.loading")} />
            </div>
          ) : errorState ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <ErrorState
                kind={errorState.kind}
                kitchenId={kitchenId}
                message={
                  errorState.kind === "network" ? errorState.message : undefined
                }
                onChangeId={() => setShowSettings(true)}
                onRetry={() => {
                  setInitialLoading(true);
                  loadTickets();
                }}
              />
            </div>
          ) : view === "archive" ? (
            <ArchiveView
              tickets={archiveTickets}
              loading={archiveLoading}
              range={archiveRange}
              onRangeChange={setArchiveRange}
            />
          ) : tickets.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <EmptyState />
            </div>
          ) : (
            /* KANBAN — dar ekranda sütunlar alt-alta, md+ üç sütun yanaşı. */
            <div className="p-4 sm:p-5 grid gap-4 md:grid-cols-3 items-start">
              {BOARD_COLUMNS.map((col) => (
                <BoardColumn
                  key={col.status}
                  title={t(col.labelKey)}
                  accent={col.accent}
                  tickets={board[col.status] ?? []}
                  onStatusChange={handleStatusChange}
                  canUpdateStatus={canUpdateStatus}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Küçük yardımcı bileşenler
// ---------------------------------------------------------------------------

const StatPill: React.FC<{
  label: string;
  value: number;
  tone: "gray" | "blue" | "green";
}> = ({ label, value, tone }) => {
  const styles = {
    gray: {
      wrap: "bg-black/[0.04] dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/5 border-black/5 dark:border-white/10",
      label: "text-gray-500 dark:text-gray-400",
      value: "text-gray-900 dark:text-white",
    },
    blue: {
      wrap: "bg-blue-500/10 border-blue-500/20",
      label: "text-blue-600 dark:text-blue-400",
      value: "text-blue-700 dark:text-blue-300",
    },
    green: {
      wrap: "bg-emerald-500/10 border-emerald-500/20",
      label: "text-emerald-600 dark:text-emerald-400",
      value: "text-emerald-700 dark:text-emerald-300",
    },
  }[tone];

  return (
    <div
      className={`px-3 py-1.5 rounded-xl border ${styles.wrap} leading-tight`}
    >
      <p
        className={`text-[9px] font-black uppercase tracking-widest ${styles.label}`}
      >
        {label}
      </p>
      <p className={`font-black text-xl ${styles.value} pos-tabular`}>
        {value}
      </p>
    </div>
  );
};

const EmptyState: React.FC = () => {
  const { t } = useI18n();
  return (
    <div className="text-center max-w-sm px-6">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
        {t("kitchen.empty.clean")}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t("kitchen.empty.noneActive")}
      </p>
    </div>
  );
};

/**
 * Kanban sütunu. Boş sütun da göstərilir — aşpaz axının hansı mərhələsində
 * boşluq olduğunu görməlidir.
 */
const BoardColumn: React.FC<{
  title: string;
  accent: string;
  tickets: KitchenTicket[];
  onStatusChange: (orderId: OrderRef, next: KitchenOrderStatus) => Promise<void> | void;
  canUpdateStatus: boolean;
}> = ({ title, accent, tickets, onStatusChange, canUpdateStatus }) => (
  <section className="min-w-0">
    <header className="flex items-center gap-2 px-1 pb-2.5">
      <span className={`w-2 h-2 rounded-full ${accent}`} />
      <h2 className="text-[13px] font-bold text-gray-700 dark:text-gray-200">{title}</h2>
      <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 pos-tabular">
        {tickets.length}
      </span>
    </header>
    <div className="space-y-3">
      {tickets.map((ticket) => (
        <TicketCard
          key={ticket.id}
          ticket={ticket}
          onStatusChange={onStatusChange}
          canUpdateStatus={canUpdateStatus}
        />
      ))}
      {tickets.length === 0 && (
        <div className="rounded-2xl border border-dashed border-black/[0.08] dark:border-white/10 py-8" />
      )}
    </div>
  </section>
);

/**
 * ARXİV — ofisantın təhvil aldığı sifarişlər.
 *
 * pos_local bütün yerli tarixçəni saxlayır; hazır aralıqlar (bu gün / dünən /
 * 7 / 30 gün) yerli bazadan oxunur.
 */
const ArchiveView: React.FC<{
  tickets: KitchenTicket[];
  loading: boolean;
  range: ArchiveRangeKey;
  onRangeChange: (key: ArchiveRangeKey) => void;
}> = ({ tickets, loading, range, onRangeChange }) => {
  const { t } = useI18n();
  const totalItems = tickets.reduce((n, ticket) => n + ticket.items.length, 0);

  return (
    <div className="p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div
          className="pos-tabstrip flex p-1 rounded-xl border border-black/5 dark:border-white/10 overflow-x-auto pos-scroll"
          style={{ backgroundColor: "var(--bg-elevated)" }}
        >
          {ARCHIVE_RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => onRangeChange(r.key)}
              className={`pos-press px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-colors ${
                range === r.key
                  ? "bg-[var(--primary)] text-white"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {t(r.labelKey)}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 pos-tabular">
          {t("kitchen.archive.summary", { orders: tickets.length, items: totalItems })}
        </span>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <PosLoader size="lg" label={t("kitchen.loading")} />
        </div>
      ) : tickets.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("kitchen.archive.empty")}
          </p>
        </div>
      ) : (
        <div
          className="grid gap-3 items-start"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))" }}
        >
          {tickets.map((ticket) => (
            <article
              key={ticket.id}
              className="rounded-2xl border border-black/[0.07] dark:border-white/10 overflow-hidden"
              style={{ backgroundColor: "var(--bg-card, #ffffff)" }}
            >
              <div className="px-4 pt-3 pb-2 flex items-baseline justify-between gap-3">
                <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                  {ticket.table || t("kitchen.channel.hall")}
                </h3>
                <span className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0 pos-tabular">
                  {ticket.served_at
                    ? new Date(ticket.served_at).toLocaleTimeString("az-AZ", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—"}
                </span>
              </div>
              <ul className="px-4 pb-3 space-y-1">
                {ticket.items.map((item, idx) => (
                  <li
                    key={`${item.id}-${idx}`}
                    className="text-[13px] text-gray-600 dark:text-gray-300 flex gap-2"
                  >
                    <span className="pos-tabular text-gray-400 dark:text-gray-500">
                      {item.quantity}×
                    </span>
                    <span className="min-w-0 break-words">{item.name}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

const ErrorState: React.FC<{
  kind: "not_found" | "network";
  kitchenId: OrderRef;
  message?: string;
  onChangeId: () => void;
  onRetry: () => void;
}> = ({ kind, kitchenId, message, onChangeId, onRetry }) => {
  const { t } = useI18n();
  return (
  <div className="text-center max-w-md px-6">
    <div className="w-24 h-24 mx-auto mb-5 rounded-[2rem] bg-red-500/10 border border-red-500/20 flex items-center justify-center">
      <AlertTriangle className="w-10 h-10 text-red-500" />
    </div>
    <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
      {kind === "not_found"
        ? t("kitchen.err.notFound", { id: kitchenId })
        : t("kitchen.connectFailed")}
    </h3>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-6">
      {kind === "not_found"
        ? t("kitchen.err.notFoundHint")
        : message || t("kitchen.err.retryHint")}
    </p>
    <div className="flex items-center justify-center gap-3">
      <button
        type="button"
        onClick={onRetry}
        className="pos-press inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-black/5 dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/10 hover:bg-black/10 dark:hover:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/15 text-gray-800 dark:text-gray-200 text-sm font-bold transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        {t("kitchen.retry")}
      </button>
      {kind === "not_found" && (
        <button
          type="button"
          onClick={onChangeId}
          className="pos-press inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--primary)] hover:bg-orange-600 text-white text-sm font-bold shadow-md shadow-orange-500/25 transition-colors"
        >
          <Settings className="w-4 h-4" />
          {t("kitchen.changeId")}
        </button>
      )}
    </div>
  </div>
  );
};

/**
 * Header'a gömülü settings buton + popover. Kullanıcı mevcut kitchen id'yi
 * görür ve yeni bir id girip kaydedebilir. Submit'te `onChange` çağrılır.
 */
const KitchenIdControl: React.FC<{
  currentId: OrderRef;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (next: number) => void;
  onRefresh: () => void;
}> = ({ currentId, isOpen, onToggle, onClose, onChange, onRefresh }) => {
  const { t } = useI18n();
  const [draft, setDraft] = useState(String(currentId));
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDraft(String(currentId));
  }, [currentId]);

  // Dışına tıklanınca kapat.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        e.target instanceof Node &&
        !popoverRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose]);

  const submit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n <= 0) {
      notify.error(t("kitchen.idPositive"));
      return;
    }
    onChange(n);
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={onToggle}
        title={t("kitchen.idChangeTitle", { id: currentId })}
        className={`pos-press inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold transition-colors ${isOpen
          ? "bg-[var(--primary)] text-white border-transparent shadow-sm"
          : "bg-black/5 dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/10 text-gray-700 dark:text-gray-200 border-black/5 dark:border-white/10 hover:bg-black/10 dark:hover:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/15"
          }`}
      >
        <Settings className="w-4 h-4" />
        <span className="pos-tabular">#{currentId}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -6 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-black/10 dark:border-white/10 shadow-2xl shadow-black/10 dark:shadow-black/40 p-4 z-50"
            style={{ backgroundColor: "var(--bg-card, #ffffff)" }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                  {t("kitchen.idLabel")}
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {t("kitchen.whichKitchen")}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/10 text-gray-400"
                aria-label={t("common.close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                  if (e.key === "Escape") onClose();
                }}
                autoFocus
                placeholder={t("kitchen.egPlaceholder")}
                className="flex-1 h-10 px-3 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/[0.04] text-gray-900 dark:text-white text-sm font-bold pos-tabular outline-none focus:border-[var(--primary)] focus:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700 dark:focus:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/[0.06] transition-colors"
              />
              <button
                type="button"
                onClick={submit}
                className="pos-press h-10 px-4 rounded-xl bg-[var(--primary)] hover:bg-orange-600 text-white text-xs font-black uppercase tracking-wide shadow-sm transition-colors"
              >
                {t("common.save")}
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                onRefresh();
                onClose();
              }}
              className="mt-3 w-full pos-press inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-black/5 dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/10 hover:bg-black/10 dark:hover:bg-white dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/15 text-gray-700 dark:text-gray-200 text-xs font-bold transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {t("kitchen.reload")}
            </button>

            <p className="mt-3 text-[10px] text-gray-400 dark:text-gray-500 leading-snug">
              {t("kitchen.tipPrefix")}{" "}
              <code className="px-1 py-0.5 rounded bg-black/[0.05] dark:bg-[#25262b] dark:text-gray-100 dark:border-gray-700/10">
                ?kitchenId=N
              </code>{" "}
              {t("kitchen.tipSuffix")}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Kitchen;
